/**
 * Standalone AAI (Answer AI) streaming client.
 *
 * Why this lives outside `useSSE`:
 *   `useSSE` is a singleton hook with a single `AbortController` ref —
 *   only one connection can be active at a time. The new round-model
 *   workflow lets a user click "Assist (AAI)" on multiple questions at
 *   once, and each click must run independently without aborting the
 *   others (or the main project SSE). This module opens its own
 *   `fetch` + ReadableStream parser per call so each Assist request
 *   gets its own AbortController, scoped to a single questionId.
 *
 * Cancellation:
 *   The caller passes in an `AbortSignal`. Aborting it stops the
 *   `fetch` and the read loop. The caller is responsible for tracking
 *   AbortControllers per-question (see `useConversation.ts`).
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

export interface StreamAAIOptions {
  projectId: string
  questionId: string
  branchId: string
  context?: string
  forceWebSearch?: boolean
  token: string | null
  signal: AbortSignal
  /** Called once for every parsed `data: {...}` SSE frame. */
  onEvent: (event: { type?: string; [key: string]: unknown }) => void
}

/**
 * Open a one-shot streaming POST to `/api/projects/{id}/aai/assist`
 * and dispatch each parsed SSE frame to `onEvent`. Resolves when the
 * stream ends; rejects on network/parse errors. Aborts return cleanly
 * (no error thrown).
 */
export async function streamAAI(opts: StreamAAIOptions): Promise<void> {
  const url = `${API_URL}/api/projects/${opts.projectId}/aai/assist`
  let response: Response
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(opts.token ? { Authorization: `Bearer ${opts.token}` } : {}),
      },
      body: JSON.stringify({
        questionId: opts.questionId,
        branchId: opts.branchId,
        context: opts.context || undefined,
        forceWebSearch: !!opts.forceWebSearch,
      }),
      signal: opts.signal,
    })
  } catch (err) {
    // AbortError shows up here when the controller fires before
    // headers come back. Treat it as a clean cancellation.
    if ((err as { name?: string })?.name === 'AbortError') return
    throw err
  }

  if (!response.ok) {
    throw new Error(`AAI HTTP ${response.status}: ${response.statusText}`)
  }

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('AAI: no response body')
  }

  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const dataStr = line.slice(6)
        try {
          const event = JSON.parse(dataStr)
          opts.onEvent(event)
        } catch {
          console.warn('[aaiClient] failed to parse SSE frame', dataStr)
        }
      }
    }
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') return
    throw err
  }
}
